import { LendingVaultExpanded, MarketAssetView, Mint, SerializeStrategyDuration } from "@bridgesplit/abf-react";
import { ExternalYieldSource, TokenListMetadata, VaultMetadataTag } from "@bridgesplit/abf-sdk";
import { EMPTY_STRING } from "@bridgesplit/utils";

export type CollateralForm = {
    search: string;
    mintToInfo: Record<string, CollateralAmountsForm>;
    // Map from collateral mint to all available market info options
    collateralToMarketInfoOptions?: Map<string, MarketAssetView[]>;
};

export type CollateralAmountsForm = {
    ltvUiAmount?: number;
    liquidationLtvAmount?: number;
    marketInformationAddress?: string;
    oracleAddress?: string;
    oracleType?: number;
};

export type RateForm = {
    search: string;
    mintToRateInfo: Record<Mint, Record<SerializeStrategyDuration, RateAmountForm>>;
};
/** apyAmountUi: percent string (e.g. "10.5"). Empty string = disabled (DISABLED_APY). */
export type RateAmountForm = {
    apy: string;
};

/** Form for vault metadata (LendingVaultMetadata). */
export type DetailForm = {
    name: string | undefined;
    description: string | undefined;
    managerName: string | undefined;
    managerImage: string | undefined;
    bannerImage: string | undefined;
    tokenImage: string | undefined;
    themeColor: string | undefined;
    tags: VaultMetadataTag[] | undefined;
    depositCap: number | undefined;
};

export type SettingForm = {
    search: string;
    viewPendingUpdate: boolean;
    idleCapital: ExternalYieldSource | undefined;
    originationsEnabled: boolean | undefined;
    // Lend vault escrow
    liquidityBuffer: number | undefined;
    originationFee: number | undefined;
    interestFee: number | undefined;
    originationCap: number | undefined;

    //Paresed lend vault terms
    liquidationThreshold: number | undefined;
    maxEarlyUnstakeFee: number | undefined;
};

// All form values are strings. Empty string = disabled (U64Max/DISABLED_APY).
export type PrincipalCapsForm = {
    max1hrBorrowCap: string;
    max24hrBorrowCap: string;
    maxOutstandingBorrowCap: string;
    max1hrSupplyCap: string;
    max24hrSupplyCap: string;
    maxOutstandingSupplyCap: string;
    max1hrWithdrawCap: string;
    max24hrWithdrawCap: string;
};

/** Values are percent strings (e.g. "10.5"). Empty string = disabled. */
export type CollateralAllocationForm = {
    search: string;
    mintToMaxAllocationPct: Record<string, string>;
};

/** Required fields: name, description, managerName, managerImage. Optional fields use undefined. */
export const initialDetailForm: DetailForm = {
    name: "",
    description: "",
    managerName: "",
    managerImage: "",
    bannerImage: undefined,
    tokenImage: undefined,
    themeColor: undefined,
    tags: undefined,
    depositCap: undefined
};

export const initialCollateralForm: CollateralForm = {
    search: EMPTY_STRING,
    mintToInfo: {}
};

export const initialRateForm: RateForm = {
    search: EMPTY_STRING,
    mintToRateInfo: {}
};

export const initialSettingsForm: SettingForm = {
    search: EMPTY_STRING,
    viewPendingUpdate: false,
    idleCapital: undefined,
    originationsEnabled: undefined,
    liquidityBuffer: undefined,
    originationFee: undefined,
    interestFee: undefined,
    originationCap: undefined,
    liquidationThreshold: undefined,
    maxEarlyUnstakeFee: undefined
};

export const initialPrincipalCapsForm: PrincipalCapsForm = {
    max1hrBorrowCap: EMPTY_STRING,
    max24hrBorrowCap: EMPTY_STRING,
    maxOutstandingBorrowCap: EMPTY_STRING,
    max1hrSupplyCap: EMPTY_STRING,
    max24hrSupplyCap: EMPTY_STRING,
    maxOutstandingSupplyCap: EMPTY_STRING,
    max1hrWithdrawCap: EMPTY_STRING,
    max24hrWithdrawCap: EMPTY_STRING
};

export const initialCollateralAllocationForm: CollateralAllocationForm = {
    search: EMPTY_STRING,
    mintToMaxAllocationPct: {}
};

export type CollateralFormTableInfo = CollateralAmountsForm & {
    id: number;
    metadata: TokenListMetadata | undefined;
};

export type LendVaultProps = {
    vaultAddress: string | undefined;
    vaultExpanded: LendingVaultExpanded | undefined;
    isLoading: boolean;
    isFetching: boolean;
    notFound: boolean;
};
